import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ScrollText,
  Search,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { formatDateTime } from "../../lib/api";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  type BadgeTone,
} from "../../components/ui";
import {
  fetchAuditActions,
  fetchAuditLog,
  type ActorType,
  type AuditLogEntry,
} from "./api";

const PAGE_SIZE = 50;

const ACTOR_TYPES: { value: ActorType | ""; label: string }[] = [
  { value: "", label: "Todos los actores" },
  { value: "staff", label: "Staff (admin/operador)" },
  { value: "superadmin", label: "Superadmin" },
  { value: "socio", label: "Socio (portal)" },
  { value: "system", label: "Sistema (cron)" },
];

export function AuditLogPage(): JSX.Element {
  const [action, setAction] = useState<string>("");
  const [actorType, setActorType] = useState<ActorType | "">("");
  const [entityType, setEntityType] = useState<string>("");
  const [entityId, setEntityId] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");
  const [page, setPage] = useState<number>(0);

  const offset = page * PAGE_SIZE;

  const filtros = useMemo(
    () => ({
      action: action || null,
      actor_type: (actorType || null) as ActorType | null,
      entity_type: entityType || null,
      entity_id: entityId ? Number(entityId) : null,
      q: q.trim() || null,
      desde: desde ? `${desde}T00:00:00` : null,
      hasta: hasta ? `${hasta}T23:59:59` : null,
      limit: PAGE_SIZE,
      offset,
    }),
    [action, actorType, entityType, entityId, q, desde, hasta, offset],
  );

  const logQ = useQuery({
    queryKey: ["audit-log", filtros],
    queryFn: () => fetchAuditLog(filtros),
  });

  const actionsQ = useQuery({
    queryKey: ["audit-actions"],
    queryFn: fetchAuditActions,
  });

  const total = logQ.data?.total ?? 0;
  const items = logQ.data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function resetFiltros(): void {
    setAction("");
    setActorType("");
    setEntityType("");
    setEntityId("");
    setQ("");
    setDesde("");
    setHasta("");
    setPage(0);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Auditoría"
        icon={ScrollText}
        subtitle={
          <>
            Registro inmutable de operaciones sensibles: anulaciones, cambios de
            estado, cierres, recupero FNDR, gestión de usuarios. Cada entrada
            guarda quién hizo el cambio, qué cambió y cuándo.
            {logQ.data && (
              <> ({total.toLocaleString("es-CL")} registros)</>
            )}
          </>
        }
      />

      {/* Atajos rápidos por tipo de actor */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-neutral-500">Filtro rápido:</span>
        {ACTOR_TYPES.map((opt) => {
          const isActive = actorType === opt.value;
          return (
            <button
              key={opt.value || "all"}
              type="button"
              aria-pressed={isActive}
              onClick={() => {
                setActorType(opt.value);
                setPage(0);
              }}
              className={
                isActive
                  ? "rounded-full bg-brand-700 px-2.5 py-1 font-medium text-white"
                  : "rounded-full bg-neutral-100 px-2.5 py-1 text-neutral-600 hover:bg-neutral-200"
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <Card padding="sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Buscar (action / entity)">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" strokeWidth={1.5} />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
                placeholder="ej: pago / cierre"
                className="w-full rounded-md border border-neutral-450 pl-7 pr-2 py-1.5 text-sm shadow-sm"
              />
            </div>
          </Field>
          <Field label="Acción">
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            >
              <option value="">Todas</option>
              {(actionsQ.data ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tipo de actor">
            <select
              value={actorType}
              onChange={(e) => {
                setActorType(e.target.value as ActorType | "");
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            >
              {ACTOR_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Entidad">
            <div className="flex gap-2">
              <input
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value);
                  setPage(0);
                }}
                placeholder="Tipo (Pago, Lectura…)"
                className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
              />
              <input
                value={entityId}
                onChange={(e) => {
                  setEntityId(e.target.value);
                  setPage(0);
                }}
                placeholder="ID"
                inputMode="numeric"
                className="w-20 rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
              />
            </div>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              value={desde}
              onChange={(e) => {
                setDesde(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              value={hasta}
              onChange={(e) => {
                setHasta(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            />
          </Field>
          <div className="sm:col-span-1 lg:col-span-2 flex items-end justify-end">
            <Button variant="link" size="sm" icon={X} onClick={resetFiltros}>
              Limpiar filtros
            </Button>
          </div>
        </div>
      </Card>

      {logQ.isPending && <Cargando texto="Cargando bitácora…" />}
      {logQ.isError && (
        <ErrorCarga
          error={logQ.error}
          mensaje="Error al cargar la bitácora."
          onReintentar={() => void logQ.refetch()}
        />
      )}

      {logQ.data && items.length === 0 && (
        <p className="text-sm text-neutral-500">
          Sin registros para los filtros aplicados.
        </p>
      )}

      {items.length > 0 && (
        <TableWrap label="Bitácora de auditoría">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="w-8 px-2 py-2"></th>
                <th scope="col" className="px-3 py-2 text-left">Cuándo</th>
                <th scope="col" className="px-3 py-2 text-left">Acción</th>
                <th scope="col" className="px-3 py-2 text-left">Entidad</th>
                <th scope="col" className="px-3 py-2 text-left">Actor</th>
                <th scope="col" className="px-3 py-2 text-left">request_id</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((entry) => (
                <AuditRow key={entry.id} entry={entry} />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {/* Paginación */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-neutral-600">
          <span>
            Página {page + 1} de {totalPages} · {total.toLocaleString("es-CL")}{" "}
            registros
          </span>
          <div className="flex items-center gap-1">
            <PagBtn
              icon={ChevronsLeft}
              label="Primera página"
              onClick={() => setPage(0)}
              disabled={page === 0}
            />
            <PagBtn
              icon={ChevronLeft}
              label="Página anterior"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            />
            <PagBtn
              icon={ChevronRight}
              label="Página siguiente"
              onClick={() =>
                setPage((p) => Math.min(totalPages - 1, p + 1))
              }
              disabled={page >= totalPages - 1}
            />
            <PagBtn
              icon={ChevronsRight}
              label="Última página"
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry }): JSX.Element {
  const [open, setOpen] = useState(false);

  const tieneDetalle = entry.changes !== null || entry.meta !== null;

  return (
    <>
      <tr className={open ? "bg-neutral-50" : "hover:bg-neutral-50"}>
        <td className="px-2 py-2 text-center">
          {tieneDetalle && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              icon={open ? ChevronDown : ChevronRight}
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Colapsar" : "Expandir"}
            />
          )}
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-neutral-700 tabular-nums">
          {formatDateTime(entry.created_at)}
        </td>
        <td className="px-3 py-2">
          <span className="inline-flex rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-mono text-neutral-700">
            {entry.action}
          </span>
        </td>
        <td className="px-3 py-2 text-neutral-600">
          {entry.entity_type ? (
            <span>
              {entry.entity_type}
              {entry.entity_id !== null && (
                <span className="text-neutral-500"> · #{entry.entity_id}</span>
              )}
            </span>
          ) : (
            <span className="text-neutral-500">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-neutral-600">
          <ActorCell entry={entry} />
        </td>
        <td className="px-3 py-2">
          <span className="font-mono text-xs text-neutral-500">
            {entry.request_id}
          </span>
        </td>
      </tr>
      {open && tieneDetalle && (
        <tr className="bg-neutral-50/60">
          <td></td>
          <td colSpan={5} className="px-3 py-3 text-xs">
            {entry.changes && (
              <div className="mb-2">
                <div className="mb-1 font-medium text-neutral-700">
                  Cambios (before → after)
                </div>
                <ChangesTable changes={entry.changes} />
              </div>
            )}
            {entry.meta && (
              <div>
                <div className="mb-1 font-medium text-neutral-700">Contexto</div>
                <pre className="whitespace-pre-wrap rounded bg-white p-2 font-mono text-xs text-neutral-700 ring-1 ring-neutral-200">
                  {JSON.stringify(entry.meta, null, 2)}
                </pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// superadmin en `amber` (Soporte SaaS), socio en `brand`, staff/system en `slate`.
const ACTOR_TONE: Record<ActorType, BadgeTone> = {
  staff: "slate",
  superadmin: "amber",
  socio: "brand",
  system: "slate",
};

const ACTOR_BADGE_LABELS: Record<ActorType, string> = {
  staff: "Staff",
  superadmin: "Soporte SaaS",
  socio: "Socio",
  system: "Sistema",
};

function ActorBadge({ actorType }: { actorType: ActorType }): JSX.Element {
  return <Badge tone={ACTOR_TONE[actorType]}>{ACTOR_BADGE_LABELS[actorType]}</Badge>;
}

function ActorCell({ entry }: { entry: AuditLogEntry }): JSX.Element {
  if (entry.actor_user_nombre) {
    return (
      <div>
        <div className="flex items-center gap-2">
          <span className="text-neutral-800">{entry.actor_user_nombre}</span>
          <ActorBadge actorType={entry.actor_type} />
        </div>
        <div className="text-xs text-neutral-500">{entry.actor_user_email}</div>
      </div>
    );
  }
  if (entry.actor_socio_id) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-neutral-700">Socio #{entry.actor_socio_id}</span>
        <ActorBadge actorType={entry.actor_type} />
      </div>
    );
  }
  return <ActorBadge actorType={entry.actor_type} />;
}

function ChangesTable({
  changes,
}: {
  changes: Record<string, unknown>;
}): JSX.Element {
  const rows = Object.entries(changes);
  return (
    <TableWrap className="rounded">
      <table className="min-w-full divide-y divide-neutral-200 text-xs">
        <thead className="bg-white text-neutral-500">
          <tr>
            <th scope="col" className="px-2 py-1 text-left font-medium">Campo</th>
            <th scope="col" className="px-2 py-1 text-left font-medium">Antes</th>
            <th scope="col" className="px-2 py-1 text-left font-medium">Después</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 bg-white">
          {rows.map(([campo, valor]) => {
            const arr = Array.isArray(valor) ? valor : [null, valor];
            const before = arr[0];
            const after = arr[1];
            return (
              <tr key={campo}>
                <td className="px-2 py-1 font-mono">{campo}</td>
                <td className="px-2 py-1 font-mono text-rose-700">
                  {fmt(before)}
                </td>
                <td className="px-2 py-1 font-mono text-emerald-700">
                  {fmt(after)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableWrap>
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function PagBtn({
  icon,
  onClick,
  disabled,
  label,
}: {
  icon: LucideIcon;
  onClick: () => void;
  disabled: boolean;
  label: string;
}): JSX.Element {
  return (
    <Button
      variant="secondary"
      iconOnly
      icon={icon}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    />
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
